import {useEffect, useMemo, useState} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {ExpandMore} from "@mui/icons-material";
import dayjs from "dayjs";
import {useTranslation} from "react-i18next";
import {EmployeeUserType} from "../../../types/employeeType";
import {GeneralButton} from "../../../common/ui/GeneralButton";
import {timeTrackingApi} from "../../../api/timeTrackingApi";
import {CreateTimeQuotaRequest, TimeQuota} from "../../../types/timeTracking";
import {HolidayExtent} from "../../../types/holidayExtent";

type WorkdaysState = {
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
};

type RegionalHolidaysState = {
  holy3KingsDay: HolidayExtent;
  internationalWomensHoliday: HolidayExtent;
  fronleichnamHoliday: HolidayExtent;
  mariaHimmelfahrtHoliday: HolidayExtent;
  worldChildrenDayHoliday: HolidayExtent;
  reformationDayHoliday: HolidayExtent;
  allerheiligenDayHoliday: HolidayExtent;
  bussUndBetTagHoliday: HolidayExtent;
  augsburgerFriedensfestHoliday: HolidayExtent;
  christmasEveHoliday: HolidayExtent;
  newYearsEveHoliday: HolidayExtent;
  easterSundayHoliday: HolidayExtent;
  pfingstSundayHoliday: HolidayExtent;
};

type WorkTimeQuotaDialogProps = {
  open: boolean;
  onClose: () => void;
  employee: EmployeeUserType;
  timeTrackingServiceModuleId?: number;
  canEdit: boolean;
  companyId: number | null;
};

const defaultWorkdays: WorkdaysState = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
};

const defaultRegionalHolidays: RegionalHolidaysState = {
  holy3KingsDay: HolidayExtent.None,
  internationalWomensHoliday: HolidayExtent.None,
  fronleichnamHoliday: HolidayExtent.None,
  mariaHimmelfahrtHoliday: HolidayExtent.None,
  worldChildrenDayHoliday: HolidayExtent.None,
  reformationDayHoliday: HolidayExtent.None,
  allerheiligenDayHoliday: HolidayExtent.None,
  bussUndBetTagHoliday: HolidayExtent.None,
  augsburgerFriedensfestHoliday: HolidayExtent.None,
  christmasEveHoliday: HolidayExtent.None,
  newYearsEveHoliday: HolidayExtent.None,
  easterSundayHoliday: HolidayExtent.None,
  pfingstSundayHoliday: HolidayExtent.None,
};

const getIsoWeek = (date: Date) => {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNumber = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil((((target.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const getIsoWeekStart = (week: number, year: number) => {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dayOfWeek = simple.getUTCDay();
  const isoWeekStart = new Date(simple);
  if (dayOfWeek <= 4 && dayOfWeek > 0) {
    isoWeekStart.setUTCDate(simple.getUTCDate() - dayOfWeek + 1);
  } else {
    isoWeekStart.setUTCDate(simple.getUTCDate() + 8 - dayOfWeek);
  }
  return isoWeekStart;
};

const getIsoWeekCount = (year: number) => {
  const endOfYear = new Date(Date.UTC(year, 11, 28));
  return getIsoWeek(endOfYear);
};

const WorkTimeQuotaDialog = ({
  open,
  onClose,
  employee,
  timeTrackingServiceModuleId,
  canEdit,
  companyId,
}: WorkTimeQuotaDialogProps) => {
  const { t } = useTranslation();
  const [weeklyHours, setWeeklyHours] = useState("");
  const [initialSurplusHours, setInitialSurplusHours] = useState("");
  const [editWeeklyHours, setEditWeeklyHours] = useState("");
  const [pausesAreWorkingTime, setPausesAreWorkingTime] = useState(false);
  const [editPausesAreWorkingTime, setEditPausesAreWorkingTime] = useState(false);
  const [createStartWeek, setCreateStartWeek] = useState<number | "">("");
  const [createStartYear, setCreateStartYear] = useState<number>(dayjs().year());
  const [createEndWeek, setCreateEndWeek] = useState<number | "">("");
  const [createEndYear, setCreateEndYear] = useState<number>(dayjs().year());
  const [workdays, setWorkdays] = useState<WorkdaysState>(defaultWorkdays);
  const [editWorkdays, setEditWorkdays] = useState<WorkdaysState>(defaultWorkdays);
  const [regionalHolidays, setRegionalHolidays] = useState<RegionalHolidaysState>(
    defaultRegionalHolidays
  );
  const [editRegionalHolidays, setEditRegionalHolidays] = useState<RegionalHolidaysState>(
    defaultRegionalHolidays
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasExistingQuotas, setHasExistingQuotas] = useState<boolean | null>(null);
  const [existingQuotas, setExistingQuotas] = useState<TimeQuota[]>([]);
  const [isCreateExpanded, setIsCreateExpanded] = useState(true);
  const [isEditExpanded, setIsEditExpanded] = useState(false);
  const [editStartQuotaId, setEditStartQuotaId] = useState<number | "">("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setWeeklyHours("");
    setInitialSurplusHours("");
    setEditWeeklyHours("");
    setPausesAreWorkingTime(false);
    setEditPausesAreWorkingTime(false);
    setCreateStartWeek("");
    setCreateStartYear(dayjs().year());
    setCreateEndWeek("");
    setCreateEndYear(dayjs().year());
    setWorkdays(defaultWorkdays);
    setEditWorkdays(defaultWorkdays);
    setRegionalHolidays(defaultRegionalHolidays);
    setEditRegionalHolidays(defaultRegionalHolidays);
    setHasExistingQuotas(null);
    setExistingQuotas([]);
    setIsCreateExpanded(false);
    setIsEditExpanded(false);
    setEditStartQuotaId("");
    setErrorMessage(null);
    setSuccessMessage(null);
  }, [open, employee.id]);

  useEffect(() => {
    if (!open || !timeTrackingServiceModuleId) return;
    const fetchQuotas = async () => {
      const result = await timeTrackingApi.getTimeQuotas(
        timeTrackingServiceModuleId,
        employee.id
      );
      if (result.success() && Array.isArray(result.result)) {
        setExistingQuotas(result.result);
        setHasExistingQuotas(result.result.length > 0);
        setIsCreateExpanded(false);
        setIsEditExpanded(false);
      } else {
        setHasExistingQuotas(null);
        setExistingQuotas([]);
        setIsCreateExpanded(false);
        setIsEditExpanded(false);
      }
    };
    fetchQuotas();
  }, [open, timeTrackingServiceModuleId, employee.id]);

  const sortedQuotas = useMemo(() => {
    const currentYear = dayjs().year();
    return existingQuotas
      .filter((quota) => quota.year === currentYear)
      .sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.calendarWeek - b.calendarWeek;
    });
  }, [existingQuotas]);
  const sortedAllQuotas = useMemo(() => {
    return [...existingQuotas].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.calendarWeek - b.calendarWeek;
    });
  }, [existingQuotas]);

  const firstQuota = sortedQuotas[0];
  const lastQuota = sortedQuotas[sortedQuotas.length - 1];
  const toHours = (minutes: number) => Math.round((minutes / 60) * 100) / 100;
  const currentYear = dayjs().year();

  const startWeekCount = useMemo(
    () => getIsoWeekCount(createStartYear),
    [createStartYear]
  );
  const endWeekCount = useMemo(
    () => getIsoWeekCount(createEndYear),
    [createEndYear]
  );
  const startWeekOptions = useMemo(
    () => Array.from({ length: startWeekCount }, (_, index) => index + 1),
    [startWeekCount]
  );
  const endWeekOptions = useMemo(
    () => Array.from({ length: endWeekCount }, (_, index) => index + 1),
    [endWeekCount]
  );

  const selectedWorkdayCount = useMemo(
    () => Object.values(workdays).filter(Boolean).length,
    [workdays]
  );

  const minutesPerDay = useMemo(() => {
    const hours = Number(weeklyHours);
    if (!weeklyHours || Number.isNaN(hours) || selectedWorkdayCount === 0) {
      return 0;
    }
    const totalMinutes = hours * 60;
    return Math.round(totalMinutes / selectedWorkdayCount);
  }, [weeklyHours, selectedWorkdayCount]);
  const editSelectedWorkdayCount = useMemo(
    () => Object.values(editWorkdays).filter(Boolean).length,
    [editWorkdays]
  );
  const editMinutesPerDay = useMemo(() => {
    const hours = Number(editWeeklyHours);
    if (!editWeeklyHours || Number.isNaN(hours) || editSelectedWorkdayCount === 0) {
      return 0;
    }
    const totalMinutes = hours * 60;
    return Math.round(totalMinutes / editSelectedWorkdayCount);
  }, [editSelectedWorkdayCount, editWeeklyHours]);

  const toggleWorkday = (key: keyof WorkdaysState) => {
    setWorkdays((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const toggleEditWorkday = (key: keyof WorkdaysState) => {
    setEditWorkdays((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    if (!open || sortedAllQuotas.length === 0) {
      setEditStartQuotaId("");
      return;
    }
    setEditStartQuotaId((prev) => {
      if (prev === "" || !sortedAllQuotas.some((quota) => quota.quotaId === prev)) {
        return sortedAllQuotas[0].quotaId;
      }
      return prev;
    });
  }, [open, sortedAllQuotas]);

  useEffect(() => {
    if (!open) return;
    const defaultStartWeek = getIsoWeek(new Date());
    setCreateStartYear(currentYear);
    setCreateStartWeek(defaultStartWeek);
    setCreateEndYear(currentYear);
    setCreateEndWeek(getIsoWeekCount(currentYear));
  }, [open, currentYear]);

  const parseHolidayExtent = (value: any): HolidayExtent => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const stringToEnum: Record<string, HolidayExtent> = {
        "None": HolidayExtent.None,
        "Half": HolidayExtent.Half,
        "Full": HolidayExtent.Full,
      };
      return stringToEnum[value] ?? HolidayExtent.None;
    }
    return HolidayExtent.None;
  };

  useEffect(() => {
    if (editStartQuotaId === "") return;
    const selectedQuota = sortedAllQuotas.find(
      (quota) => quota.quotaId === editStartQuotaId
    );
    if (!selectedQuota) return;
    const totalMinutes = [
      selectedQuota.minutesMonday,
      selectedQuota.minutesTuesday,
      selectedQuota.minutesWednesday,
      selectedQuota.minutesThursday,
      selectedQuota.minutesFriday,
      selectedQuota.minutesSaturday,
      selectedQuota.minutesSunday,
    ].reduce((sum, minutes) => sum + minutes, 0);
    setEditWeeklyHours(totalMinutes > 0 ? String(toHours(totalMinutes)) : "");
    setEditWorkdays({
      monday: selectedQuota.minutesMonday > 0,
      tuesday: selectedQuota.minutesTuesday > 0,
      wednesday: selectedQuota.minutesWednesday > 0,
      thursday: selectedQuota.minutesThursday > 0,
      friday: selectedQuota.minutesFriday > 0,
      saturday: selectedQuota.minutesSaturday > 0,
      sunday: selectedQuota.minutesSunday > 0,
    });
    setEditRegionalHolidays({
      holy3KingsDay: parseHolidayExtent(selectedQuota.holy3KingsDayHoliday),
      internationalWomensHoliday: parseHolidayExtent(selectedQuota.internationalWomensHoliday),
      fronleichnamHoliday: parseHolidayExtent(selectedQuota.fronleichnamHoliday),
      mariaHimmelfahrtHoliday: parseHolidayExtent(selectedQuota.mariaHimmelfahrtHoliday),
      worldChildrenDayHoliday: parseHolidayExtent(selectedQuota.worldChildrenDayHoliday),
      reformationDayHoliday: parseHolidayExtent(selectedQuota.reformationDayHoliday),
      allerheiligenDayHoliday: parseHolidayExtent(selectedQuota.allerheiligenDayHoliday),
      bussUndBetTagHoliday: parseHolidayExtent(selectedQuota.bussUndBetTagHoliday),
      augsburgerFriedensfestHoliday: parseHolidayExtent(selectedQuota.augsburgerFriedensfestHoliday),
      christmasEveHoliday: parseHolidayExtent(selectedQuota.christmasEveHoliday),
      newYearsEveHoliday: parseHolidayExtent(selectedQuota.newYearsEveHoliday),
      easterSundayHoliday: parseHolidayExtent(selectedQuota.easterSundayHoliday),
      pfingstSundayHoliday: parseHolidayExtent(selectedQuota.pfingstSundayHoliday),
    });
    setEditPausesAreWorkingTime(selectedQuota.pausesAreWorkingTime ?? false);
  }, [editStartQuotaId, sortedAllQuotas, isEditExpanded]);

  const handleSubmit = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!canEdit) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.noPermission"));
      return;
    }

    if (!timeTrackingServiceModuleId) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.missingModule"));
      return;
    }

    if (!companyId) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.missingCompany"));
      return;
    }

    if (
      createStartWeek === ""
      || createEndWeek === ""
      || createEndYear < createStartYear
      || (createEndYear === createStartYear && createEndWeek < createStartWeek)
    ) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.invalidDateRange"));
      return;
    }

    const hours = Number(weeklyHours);
    if (!weeklyHours || Number.isNaN(hours) || hours <= 0) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.invalidWeeklyHours"));
      return;
    }

    if (selectedWorkdayCount === 0) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.noWorkdays"));
      return;
    }

    let surplusMinutes = 0;
    if (hasExistingQuotas === false) {
      const surplusHours = Number(initialSurplusHours);
      if (initialSurplusHours === "" || Number.isNaN(surplusHours)) {
        setErrorMessage(t("employeesPage.workTimeQuota.errors.invalidSurplusHours"));
        return;
      }
      surplusMinutes = Math.round(surplusHours * 60);
    }

    const startDate = getIsoWeekStart(createStartWeek, createStartYear);
    const endDate = getIsoWeekStart(createEndWeek, createEndYear);
    endDate.setUTCDate(endDate.getUTCDate() + 6);
    const dateFrom = dayjs(startDate).format("YYYY-MM-DD");
    const dateUntil = dayjs(endDate).format("YYYY-MM-DD");

    const payload: CreateTimeQuotaRequest = {
      employeeUserId: employee.id,
      companyId,
      minutesMonday: workdays.monday ? minutesPerDay : 0,
      minutesTuesday: workdays.tuesday ? minutesPerDay : 0,
      minutesWednesday: workdays.wednesday ? minutesPerDay : 0,
      minutesThursday: workdays.thursday ? minutesPerDay : 0,
      minutesFriday: workdays.friday ? minutesPerDay : 0,
      minutesSaturday: workdays.saturday ? minutesPerDay : 0,
      minutesSunday: workdays.sunday ? minutesPerDay : 0,
      createFrom: dateFrom,
      createUntil: dateUntil,
      holy3KingsDayHoliday: regionalHolidays.holy3KingsDay,
      internationalWomensHoliday: regionalHolidays.internationalWomensHoliday,
      fronleichnamHoliday: regionalHolidays.fronleichnamHoliday,
      mariaHimmelfahrtHoliday: regionalHolidays.mariaHimmelfahrtHoliday,
      worldChildrenDayHoliday: regionalHolidays.worldChildrenDayHoliday,
      reformationDayHoliday: regionalHolidays.reformationDayHoliday,
      allerheiligenDayHoliday: regionalHolidays.allerheiligenDayHoliday,
      bussUndBetTagHoliday: regionalHolidays.bussUndBetTagHoliday,
      augsburgerFriedensfestHoliday: regionalHolidays.augsburgerFriedensfestHoliday,
      christmasEveHoliday: regionalHolidays.christmasEveHoliday,
      newYearsEveHoliday: regionalHolidays.newYearsEveHoliday,
      easterSundayHoliday: regionalHolidays.easterSundayHoliday,
      pfingstSundayHoliday: regionalHolidays.pfingstSundayHoliday,
      pausesAreWorkingTime,
    };

    if (hasExistingQuotas === false) {
      payload.initialSurplusMinutes = surplusMinutes;
    }

    try {
      setIsSubmitting(true);
      const result = await timeTrackingApi.createTimeQuota(
        timeTrackingServiceModuleId,
        payload
      );
      if (!result.success()) {
        setErrorMessage(
          result.errorMessage ??
            t("employeesPage.workTimeQuota.errors.generic")
        );
        return;
      }

      setSuccessMessage(t("employeesPage.workTimeQuota.success"));
      onClose();
    } catch (error) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.generic"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!canEdit) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.noPermission"));
      return;
    }

    if (!timeTrackingServiceModuleId) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.missingModule"));
      return;
    }

    if (editStartQuotaId === "") {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.missingWeek"));
      return;
    }

    const hours = Number(editWeeklyHours);
    if (!editWeeklyHours || Number.isNaN(hours) || hours <= 0) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.invalidWeeklyHours"));
      return;
    }

    if (editSelectedWorkdayCount === 0) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.noWorkdays"));
      return;
    }

    const selectedIndex = sortedAllQuotas.findIndex(
      (quota) => quota.quotaId === editStartQuotaId
    );
    if (selectedIndex < 0) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.missingWeek"));
      return;
    }

    const quotasToUpdate = sortedAllQuotas.slice(selectedIndex);
    if (quotasToUpdate.length === 0) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.missingWeek"));
      return;
    }

    const payload = {
      minutesMonday: editWorkdays.monday ? editMinutesPerDay : 0,
      minutesTuesday: editWorkdays.tuesday ? editMinutesPerDay : 0,
      minutesWednesday: editWorkdays.wednesday ? editMinutesPerDay : 0,
      minutesThursday: editWorkdays.thursday ? editMinutesPerDay : 0,
      minutesFriday: editWorkdays.friday ? editMinutesPerDay : 0,
      minutesSaturday: editWorkdays.saturday ? editMinutesPerDay : 0,
      minutesSunday: editWorkdays.sunday ? editMinutesPerDay : 0,
      holy3KingsDayHoliday: editRegionalHolidays.holy3KingsDay,
      internationalWomensHoliday: editRegionalHolidays.internationalWomensHoliday,
      fronleichnamHoliday: editRegionalHolidays.fronleichnamHoliday,
      mariaHimmelfahrtHoliday: editRegionalHolidays.mariaHimmelfahrtHoliday,
      worldChildrenDayHoliday: editRegionalHolidays.worldChildrenDayHoliday,
      reformationDayHoliday: editRegionalHolidays.reformationDayHoliday,
      allerheiligenDayHoliday: editRegionalHolidays.allerheiligenDayHoliday,
      bussUndBetTagHoliday: editRegionalHolidays.bussUndBetTagHoliday,
      christmasEveHoliday: editRegionalHolidays.christmasEveHoliday,
      newYearsEveHoliday: editRegionalHolidays.newYearsEveHoliday,
      easterSundayHoliday: editRegionalHolidays.easterSundayHoliday,
      pfingstSundayHoliday: editRegionalHolidays.pfingstSundayHoliday,
      augsburgerFriedensfestHoliday: editRegionalHolidays.augsburgerFriedensfestHoliday,
      pausesAreWorkingTime: editPausesAreWorkingTime,
    };

    try {
      setIsUpdating(true);
      for (const quota of quotasToUpdate) {
        const result = await timeTrackingApi.updateTimeQuota(
          timeTrackingServiceModuleId,
          quota.quotaId,
          employee.id,
          payload
        );
        if (!result.success()) {
          setErrorMessage(
            result.errorMessage ?? t("employeesPage.workTimeQuota.errors.generic")
          );
          return;
        }
      }
      setSuccessMessage(t("employeesPage.workTimeQuota.updateSuccess"));
      onClose();
    } catch (error) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.generic"));
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("employeesPage.workTimeQuota.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {successMessage && <Alert severity="success">{successMessage}</Alert>}

          {hasExistingQuotas && firstQuota && lastQuota && (
            <Stack spacing={0.75}>
              <Typography variant="h6">
                {t("employeesPage.workTimeQuota.existingTitle")}
              </Typography>
              <Typography variant="subtitle1">
                {t("employeesPage.workTimeQuota.existingFirst", {
                  week: firstQuota.calendarWeek,
                  year: firstQuota.year,
                  hours: toHours(firstQuota.plannedWorkMinutes),
                })}
              </Typography>
              <Typography variant="subtitle1">
                {t("employeesPage.workTimeQuota.existingLast", {
                  week: lastQuota.calendarWeek,
                  year: lastQuota.year,
                  hours: toHours(lastQuota.plannedWorkMinutes),
                })}
              </Typography>
            </Stack>
          )}

          <Accordion
            expanded={isCreateExpanded}
            onChange={(_, expanded) => setIsCreateExpanded(expanded)}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">
                {t("employeesPage.workTimeQuota.createTitle")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <TextField
                  label={t("employeesPage.workTimeQuota.weeklyHours")}
                  type="number"
                  value={weeklyHours}
                  onChange={(event) => setWeeklyHours(event.target.value)}
                  placeholder={t("employeesPage.workTimeQuota.weeklyHoursPlaceholder")}
                  inputProps={{ min: 0, step: 0.25 }}
                  fullWidth
                />
                {hasExistingQuotas === false && (
                  <TextField
                    label={t("employeesPage.workTimeQuota.initialSurplusHours")}
                    type="number"
                    value={initialSurplusHours}
                    onChange={(event) => setInitialSurplusHours(event.target.value)}
                    inputProps={{ min: 0, step: 0.25 }}
                    fullWidth
                  />
                )}

                <Stack direction="row" spacing={2}>
                  <TextField
                    select
                    label={t("employeesPage.workTimeQuota.createFromYear")}
                    value={createStartYear}
                    onChange={(event) => {
                      const nextYear = Number(event.target.value);
                      if (Number.isFinite(nextYear)) {
                        setCreateStartYear(nextYear);
                        if (createEndYear < nextYear) {
                          setCreateEndYear(nextYear);
                        }
                      }
                    }}
                    fullWidth
                  >
                    {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((year) => (
                      <MenuItem key={`create-start-year-${year}`} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={t("employeesPage.workTimeQuota.createFromWeek")}
                    value={createStartWeek}
                    onChange={(event) => {
                      const nextValue = Number(event.target.value);
                      const nextWeek = Number.isFinite(nextValue) ? nextValue : "";
                      setCreateStartWeek(nextWeek);
                      if (nextWeek !== "" && createEndWeek !== "" && nextWeek > createEndWeek) {
                        setCreateEndWeek(nextWeek);
                      }
                    }}
                    fullWidth
                  >
                    {startWeekOptions.map((week) => (
                      <MenuItem key={`create-start-${week}`} value={week}>
                        {t("employeesPage.workTimeQuota.weekLabel", {
                          week,
                          year: createStartYear,
                        })}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={t("employeesPage.workTimeQuota.createUntilYear")}
                    value={createEndYear}
                    onChange={(event) => {
                      const nextYear = Number(event.target.value);
                      if (Number.isFinite(nextYear)) {
                        setCreateEndYear(nextYear);
                        if (createStartYear > nextYear) {
                          setCreateStartYear(nextYear);
                        }
                      }
                    }}
                    fullWidth
                  >
                    {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((year) => (
                      <MenuItem key={`create-end-year-${year}`} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={t("employeesPage.workTimeQuota.createUntilWeek")}
                    value={createEndWeek}
                    onChange={(event) => {
                      const nextValue = Number(event.target.value);
                      setCreateEndWeek(Number.isFinite(nextValue) ? nextValue : "");
                    }}
                    fullWidth
                  >
                    {endWeekOptions
                      .filter((week) =>
                        createEndYear > createStartYear
                          ? true
                          : createStartWeek === "" || week >= createStartWeek
                      )
                      .map((week) => (
                        <MenuItem key={`create-end-${week}`} value={week}>
                          {t("employeesPage.workTimeQuota.weekLabel", {
                            week,
                            year: createEndYear,
                          })}
                        </MenuItem>
                      ))}
                  </TextField>
                </Stack>

                <Stack spacing={1}>
                  <Typography variant="subtitle1">
                    {t("employeesPage.workTimeQuota.workdays")}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={workdays.monday}
                          onChange={() => toggleWorkday("monday")}
                        />
                      }
                      label={t("employeesPage.workTimeQuota.days.monday")}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={workdays.tuesday}
                          onChange={() => toggleWorkday("tuesday")}
                        />
                      }
                      label={t("employeesPage.workTimeQuota.days.tuesday")}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={workdays.wednesday}
                          onChange={() => toggleWorkday("wednesday")}
                        />
                      }
                      label={t("employeesPage.workTimeQuota.days.wednesday")}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={workdays.thursday}
                          onChange={() => toggleWorkday("thursday")}
                        />
                      }
                      label={t("employeesPage.workTimeQuota.days.thursday")}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={workdays.friday}
                          onChange={() => toggleWorkday("friday")}
                        />
                      }
                      label={t("employeesPage.workTimeQuota.days.friday")}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={workdays.saturday}
                          onChange={() => toggleWorkday("saturday")}
                        />
                      }
                      label={t("employeesPage.workTimeQuota.days.saturday")}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={workdays.sunday}
                          onChange={() => toggleWorkday("sunday")}
                        />
                      }
                      label={t("employeesPage.workTimeQuota.days.sunday")}
                    />
                  </Stack>
                  <Typography variant="body2" color="textSecondary">
                    {t("employeesPage.workTimeQuota.minutesPerDay", {
                      minutes: minutesPerDay,
                    })}
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  <Typography variant="subtitle1">
                    {t("employeesPage.workTimeQuota.regionalHolidays")}
                  </Typography>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body1">
                      {t("employeesPage.workTimeQuota.holidays.augsburgerFriedensfest")}
                    </Typography>
                    <FormControl>
                      <RadioGroup row
                                  value={regionalHolidays.augsburgerFriedensfestHoliday ?? HolidayExtent.None}
                                  onChange={(event) => {
                                    let holiday: HolidayExtent = parseInt(event.target.value);
                                    setRegionalHolidays((prev) => ({ ...prev, augsburgerFriedensfestHoliday: holiday }))
                                  }}>
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.None}
                                          label={t("holidayExtent.none")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Half}
                                          label={t("holidayExtent.half")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Full}
                                          label={t("holidayExtent.full")} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body1">
                      {t("employeesPage.workTimeQuota.holidays.christmasEve")}
                    </Typography>
                    <FormControl>
                      <RadioGroup row
                                  value={regionalHolidays.christmasEveHoliday ?? HolidayExtent.None}
                                  onChange={(event) => {
                                    let holiday: HolidayExtent = parseInt(event.target.value);
                                    setRegionalHolidays((prev) => ({ ...prev, christmasEveHoliday: holiday }))
                                  }}>
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.None}
                                          label={t("holidayExtent.none")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Half}
                                          label={t("holidayExtent.half")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Full}
                                          label={t("holidayExtent.full")} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body1">
                      {t("employeesPage.workTimeQuota.holidays.newYearsEve")}
                    </Typography>
                    <FormControl>
                      <RadioGroup row
                                  value={regionalHolidays.newYearsEveHoliday ?? HolidayExtent.None}
                                  onChange={(event) => {
                                    let holiday: HolidayExtent = parseInt(event.target.value);
                                    setRegionalHolidays((prev) => ({ ...prev, newYearsEveHoliday: holiday }))
                                  }}>
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.None}
                                          label={t("holidayExtent.none")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Half}
                                          label={t("holidayExtent.half")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Full}
                                          label={t("holidayExtent.full")} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body1">
                      {t("employeesPage.workTimeQuota.holidays.fronleichnam")}
                    </Typography>
                    <FormControl>
                      <RadioGroup row
                                  value={regionalHolidays.fronleichnamHoliday ?? HolidayExtent.None}
                                  onChange={(event) => {
                                    let holiday: HolidayExtent = parseInt(event.target.value);
                                    setRegionalHolidays((prev) => ({ ...prev, fronleichnamHoliday: holiday }))
                                  }}>
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.None}
                                          label={t("holidayExtent.none")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Half}
                                          label={t("holidayExtent.half")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Full}
                                          label={t("holidayExtent.full")} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body1">
                      {t("employeesPage.workTimeQuota.holidays.allerheiligen")}
                    </Typography>
                    <FormControl>
                      <RadioGroup row
                                  value={regionalHolidays.allerheiligenDayHoliday ?? HolidayExtent.None}
                                  onChange={(event) => {
                                    let holiday: HolidayExtent = parseInt(event.target.value);
                                    setRegionalHolidays((prev) => ({ ...prev, allerheiligenDayHoliday: holiday }))
                                  }}>
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.None}
                                          label={t("holidayExtent.none")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Half}
                                          label={t("holidayExtent.half")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Full}
                                          label={t("holidayExtent.full")} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body1">
                      {t("employeesPage.workTimeQuota.holidays.internationalWomensHoliday")}
                    </Typography>
                    <FormControl>
                      <RadioGroup row
                                  value={regionalHolidays.internationalWomensHoliday ?? HolidayExtent.None}
                                  onChange={(event) => {
                                    let holiday: HolidayExtent = parseInt(event.target.value);
                                    setRegionalHolidays((prev) => ({ ...prev, internationalWomensHoliday: holiday }))
                                  }}>
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.None}
                                          label={t("holidayExtent.none")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Half}
                                          label={t("holidayExtent.half")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Full}
                                          label={t("holidayExtent.full")} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body1">
                      {t("employeesPage.workTimeQuota.holidays.reformationDay")}
                    </Typography>
                    <FormControl>
                      <RadioGroup row
                                  value={regionalHolidays.reformationDayHoliday ?? HolidayExtent.None}
                                  onChange={(event) => {
                                    let holiday: HolidayExtent = parseInt(event.target.value);
                                    setRegionalHolidays((prev) => ({ ...prev, reformationDayHoliday: holiday }))
                                  }}>
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.None}
                                          label={t("holidayExtent.none")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Half}
                                          label={t("holidayExtent.half")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Full}
                                          label={t("holidayExtent.full")} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body1">
                      {t("employeesPage.workTimeQuota.holidays.bussUndBetTag")}
                    </Typography>
                    <FormControl>
                      <RadioGroup row
                                  value={regionalHolidays.bussUndBetTagHoliday ?? HolidayExtent.None}
                                  onChange={(event) => {
                                    let holiday: HolidayExtent = parseInt(event.target.value);
                                    setRegionalHolidays((prev) => ({ ...prev, bussUndBetTagHoliday: holiday }))
                                  }}>
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.None}
                                          label={t("holidayExtent.none")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Half}
                                          label={t("holidayExtent.half")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Full}
                                          label={t("holidayExtent.full")} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body1">
                      {t("employeesPage.workTimeQuota.holidays.holy3KingsDay")}
                    </Typography>
                    <FormControl>
                      <RadioGroup row
                                  value={regionalHolidays.holy3KingsDay ?? HolidayExtent.None}
                                  onChange={(event) => {
                                    let holiday: HolidayExtent = parseInt(event.target.value);
                                    setRegionalHolidays((prev) => ({ ...prev, holy3KingsDay: holiday }))
                                  }}>
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.None}
                                          label={t("holidayExtent.none")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Half}
                                          label={t("holidayExtent.half")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Full}
                                          label={t("holidayExtent.full")} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body1">
                      {t("employeesPage.workTimeQuota.holidays.mariaHimmelfahrt")}
                    </Typography>
                    <FormControl>
                      <RadioGroup row
                                  value={regionalHolidays.mariaHimmelfahrtHoliday ?? HolidayExtent.None}
                                  onChange={(event) => {
                                    let holiday: HolidayExtent = parseInt(event.target.value);
                                    setRegionalHolidays((prev) => ({ ...prev, mariaHimmelfahrtHoliday: holiday }))
                                  }}>
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.None}
                                          label={t("holidayExtent.none")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Half}
                                          label={t("holidayExtent.half")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Full}
                                          label={t("holidayExtent.full")} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body1">
                      {t("employeesPage.workTimeQuota.holidays.worldChildrenDay")}
                    </Typography>
                    <FormControl>
                      <RadioGroup row
                                  value={regionalHolidays.worldChildrenDayHoliday ?? HolidayExtent.None}
                                  onChange={(event) => {
                                    let holiday: HolidayExtent = parseInt(event.target.value);
                                    setRegionalHolidays((prev) => ({ ...prev, worldChildrenDayHoliday: holiday }))
                                  }}>
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.None}
                                          label={t("holidayExtent.none")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Half}
                                          label={t("holidayExtent.half")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Full}
                                          label={t("holidayExtent.full")} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body1">
                      {t("employeesPage.workTimeQuota.holidays.easterSunday")}
                    </Typography>
                    <FormControl>
                      <RadioGroup row
                                  value={regionalHolidays.easterSundayHoliday ?? HolidayExtent.None}
                                  onChange={(event) => {
                                    let holiday: HolidayExtent = parseInt(event.target.value);
                                    setRegionalHolidays((prev) => ({ ...prev, easterSundayHoliday: holiday }))
                                  }}>
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.None}
                                          label={t("holidayExtent.none")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Half}
                                          label={t("holidayExtent.half")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Full}
                                          label={t("holidayExtent.full")} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body1">
                      {t("employeesPage.workTimeQuota.holidays.pfingstSunday")}
                    </Typography>
                    <FormControl>
                      <RadioGroup row
                                  value={regionalHolidays.pfingstSundayHoliday ?? HolidayExtent.None}
                                  onChange={(event) => {
                                    let holiday: HolidayExtent = parseInt(event.target.value);
                                    setRegionalHolidays((prev) => ({ ...prev, pfingstSundayHoliday: holiday }))
                                  }}>
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.None}
                                          label={t("holidayExtent.none")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Half}
                                          label={t("holidayExtent.half")} />
                        <FormControlLabel control={<Radio />}
                                          value={HolidayExtent.Full}
                                          label={t("holidayExtent.full")} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={pausesAreWorkingTime}
                      onChange={(event) => setPausesAreWorkingTime(event.target.checked)}
                    />
                  }
                  label={t("employeesPage.workTimeQuota.pausesAreWorkingTime")}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          {hasExistingQuotas && (
            <Accordion
              expanded={isEditExpanded}
              onChange={(_, expanded) => setIsEditExpanded(expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">
                  {t("employeesPage.workTimeQuota.editTitle")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <TextField
                    select
                    label={t("employeesPage.workTimeQuota.editFromWeek")}
                    value={editStartQuotaId}
                    onChange={(event) => {
                      const nextValue = Number(event.target.value);
                      setEditStartQuotaId(Number.isFinite(nextValue) ? nextValue : "");
                    }}
                    fullWidth
                  >
                    {sortedAllQuotas.map((quota) => (
                      <MenuItem key={quota.quotaId} value={quota.quotaId}>
                        {t("employeesPage.workTimeQuota.weekLabel", {
                          week: quota.calendarWeek,
                          year: quota.year,
                        })}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    label={t("employeesPage.workTimeQuota.weeklyHours")}
                    type="number"
                    value={editWeeklyHours}
                    onChange={(event) => setEditWeeklyHours(event.target.value)}
                    placeholder={t("employeesPage.workTimeQuota.weeklyHoursPlaceholder")}
                    inputProps={{ min: 0, step: 0.25 }}
                    fullWidth
                  />

                  <Stack spacing={1}>
                    <Typography variant="subtitle1">
                      {t("employeesPage.workTimeQuota.workdays")}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editWorkdays.monday}
                            onChange={() => toggleEditWorkday("monday")}
                          />
                        }
                        label={t("employeesPage.workTimeQuota.days.monday")}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editWorkdays.tuesday}
                            onChange={() => toggleEditWorkday("tuesday")}
                          />
                        }
                        label={t("employeesPage.workTimeQuota.days.tuesday")}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editWorkdays.wednesday}
                            onChange={() => toggleEditWorkday("wednesday")}
                          />
                        }
                        label={t("employeesPage.workTimeQuota.days.wednesday")}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editWorkdays.thursday}
                            onChange={() => toggleEditWorkday("thursday")}
                          />
                        }
                        label={t("employeesPage.workTimeQuota.days.thursday")}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editWorkdays.friday}
                            onChange={() => toggleEditWorkday("friday")}
                          />
                        }
                        label={t("employeesPage.workTimeQuota.days.friday")}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editWorkdays.saturday}
                            onChange={() => toggleEditWorkday("saturday")}
                          />
                        }
                        label={t("employeesPage.workTimeQuota.days.saturday")}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editWorkdays.sunday}
                            onChange={() => toggleEditWorkday("sunday")}
                          />
                        }
                        label={t("employeesPage.workTimeQuota.days.sunday")}
                      />
                    </Stack>
                    <Typography variant="body2" color="textSecondary">
                      {t("employeesPage.workTimeQuota.minutesPerDay", {
                        minutes: editMinutesPerDay,
                      })}
                    </Typography>
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant="subtitle1">
                      {t("employeesPage.workTimeQuota.regionalHolidays")}
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="body1">
                        {t("employeesPage.workTimeQuota.holidays.augsburgerFriedensfest")}
                      </Typography>
                      <FormControl>
                        <RadioGroup row
                                    value={editRegionalHolidays.augsburgerFriedensfestHoliday ?? HolidayExtent.None}
                                    onChange={(event) => {
                                      let holiday: HolidayExtent = parseInt(event.target.value);
                                      setEditRegionalHolidays((prev) => ({ ...prev, augsburgerFriedensfestHoliday: holiday }))
                                    }}>
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.None}
                                            label={t("holidayExtent.none")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Half}
                                            label={t("holidayExtent.half")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Full}
                                            label={t("holidayExtent.full")} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="body1">
                        {t("employeesPage.workTimeQuota.holidays.christmasEve")}
                      </Typography>
                      <FormControl>
                        <RadioGroup row
                                    value={editRegionalHolidays.christmasEveHoliday ?? HolidayExtent.None}
                                    onChange={(event) => {
                                      let holiday: HolidayExtent = parseInt(event.target.value);
                                      setEditRegionalHolidays((prev) => ({ ...prev, christmasEveHoliday: holiday }))
                                    }}>
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.None}
                                            label={t("holidayExtent.none")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Half}
                                            label={t("holidayExtent.half")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Full}
                                            label={t("holidayExtent.full")} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="body1">
                        {t("employeesPage.workTimeQuota.holidays.newYearsEve")}
                      </Typography>
                      <FormControl>
                        <RadioGroup row
                                    value={editRegionalHolidays.newYearsEveHoliday ?? HolidayExtent.None}
                                    onChange={(event) => {
                                      let holiday: HolidayExtent = parseInt(event.target.value);
                                      setEditRegionalHolidays((prev) => ({ ...prev, newYearsEveHoliday: holiday }))
                                    }}>
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.None}
                                            label={t("holidayExtent.none")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Half}
                                            label={t("holidayExtent.half")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Full}
                                            label={t("holidayExtent.full")} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="body1">
                        {t("employeesPage.workTimeQuota.holidays.fronleichnam")}
                      </Typography>
                      <FormControl>
                        <RadioGroup row
                                    value={editRegionalHolidays.fronleichnamHoliday ?? HolidayExtent.None}
                                    onChange={(event) => {
                                      let holiday: HolidayExtent = parseInt(event.target.value);
                                      setEditRegionalHolidays((prev) => ({ ...prev, fronleichnamHoliday: holiday }))
                                    }}>
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.None}
                                            label={t("holidayExtent.none")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Half}
                                            label={t("holidayExtent.half")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Full}
                                            label={t("holidayExtent.full")} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="body1">
                        {t("employeesPage.workTimeQuota.holidays.allerheiligen")}
                      </Typography>
                      <FormControl>
                        <RadioGroup row
                                    value={editRegionalHolidays.allerheiligenDayHoliday ?? HolidayExtent.None}
                                    onChange={(event) => {
                                      let holiday: HolidayExtent = parseInt(event.target.value);
                                      setEditRegionalHolidays((prev) => ({ ...prev, allerheiligenDayHoliday: holiday }))
                                    }}>
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.None}
                                            label={t("holidayExtent.none")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Half}
                                            label={t("holidayExtent.half")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Full}
                                            label={t("holidayExtent.full")} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="body1">
                        {t("employeesPage.workTimeQuota.holidays.internationalWomensHoliday")}
                      </Typography>
                      <FormControl>
                        <RadioGroup row
                                    value={editRegionalHolidays.internationalWomensHoliday ?? HolidayExtent.None}
                                    onChange={(event) => {
                                      let holiday: HolidayExtent = parseInt(event.target.value);
                                      setEditRegionalHolidays((prev) => ({ ...prev, internationalWomensHoliday: holiday }))
                                    }}>
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.None}
                                            label={t("holidayExtent.none")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Half}
                                            label={t("holidayExtent.half")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Full}
                                            label={t("holidayExtent.full")} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="body1">
                        {t("employeesPage.workTimeQuota.holidays.reformationDay")}
                      </Typography>
                      <FormControl>
                        <RadioGroup row
                                    value={editRegionalHolidays.reformationDayHoliday ?? HolidayExtent.None}
                                    onChange={(event) => {
                                      let holiday: HolidayExtent = parseInt(event.target.value);
                                      setEditRegionalHolidays((prev) => ({ ...prev, reformationDayHoliday: holiday }))
                                    }}>
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.None}
                                            label={t("holidayExtent.none")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Half}
                                            label={t("holidayExtent.half")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Full}
                                            label={t("holidayExtent.full")} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="body1">
                        {t("employeesPage.workTimeQuota.holidays.bussUndBetTag")}
                      </Typography>
                      <FormControl>
                        <RadioGroup row
                                    value={editRegionalHolidays.bussUndBetTagHoliday ?? HolidayExtent.None}
                                    onChange={(event) => {
                                      let holiday: HolidayExtent = parseInt(event.target.value);
                                      setEditRegionalHolidays((prev) => ({ ...prev, bussUndBetTagHoliday: holiday }))
                                    }}>
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.None}
                                            label={t("holidayExtent.none")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Half}
                                            label={t("holidayExtent.half")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Full}
                                            label={t("holidayExtent.full")} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="body1">
                        {t("employeesPage.workTimeQuota.holidays.holy3KingsDay")}
                      </Typography>
                      <FormControl>
                        <RadioGroup row
                                    value={editRegionalHolidays.holy3KingsDay ?? HolidayExtent.None}
                                    onChange={(event) => {
                                      let holiday: HolidayExtent = parseInt(event.target.value);
                                      setEditRegionalHolidays((prev) => ({ ...prev, holy3KingsDay: holiday }))
                                    }}>
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.None}
                                            label={t("holidayExtent.none")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Half}
                                            label={t("holidayExtent.half")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Full}
                                            label={t("holidayExtent.full")} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="body1">
                        {t("employeesPage.workTimeQuota.holidays.mariaHimmelfahrt")}
                      </Typography>
                      <FormControl>
                        <RadioGroup row
                                    value={editRegionalHolidays.mariaHimmelfahrtHoliday ?? HolidayExtent.None}
                                    onChange={(event) => {
                                      let holiday: HolidayExtent = parseInt(event.target.value);
                                      setEditRegionalHolidays((prev) => ({ ...prev, mariaHimmelfahrtHoliday: holiday }))
                                    }}>
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.None}
                                            label={t("holidayExtent.none")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Half}
                                            label={t("holidayExtent.half")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Full}
                                            label={t("holidayExtent.full")} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="body1">
                        {t("employeesPage.workTimeQuota.holidays.worldChildrenDay")}
                      </Typography>
                      <FormControl>
                        <RadioGroup row
                                    value={editRegionalHolidays.worldChildrenDayHoliday ?? HolidayExtent.None}
                                    onChange={(event) => {
                                      let holiday: HolidayExtent = parseInt(event.target.value);
                                      setEditRegionalHolidays((prev) => ({ ...prev, worldChildrenDayHoliday: holiday }))
                                    }}>
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.None}
                                            label={t("holidayExtent.none")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Half}
                                            label={t("holidayExtent.half")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Full}
                                            label={t("holidayExtent.full")} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="body1">
                        {t("employeesPage.workTimeQuota.holidays.easterSunday")}
                      </Typography>
                      <FormControl>
                        <RadioGroup row
                                    value={editRegionalHolidays.easterSundayHoliday ?? HolidayExtent.None}
                                    onChange={(event) => {
                                      let holiday: HolidayExtent = parseInt(event.target.value);
                                      setEditRegionalHolidays((prev) => ({ ...prev, easterSundayHoliday: holiday }))
                                    }}>
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.None}
                                            label={t("holidayExtent.none")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Half}
                                            label={t("holidayExtent.half")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Full}
                                            label={t("holidayExtent.full")} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="body1">
                        {t("employeesPage.workTimeQuota.holidays.pfingstSunday")}
                      </Typography>
                      <FormControl>
                        <RadioGroup row
                                    value={editRegionalHolidays.pfingstSundayHoliday ?? HolidayExtent.None}
                                    onChange={(event) => {
                                      let holiday: HolidayExtent = parseInt(event.target.value);
                                      setEditRegionalHolidays((prev) => ({ ...prev, pfingstSundayHoliday: holiday }))
                                    }}>
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.None}
                                            label={t("holidayExtent.none")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Half}
                                            label={t("holidayExtent.half")} />
                          <FormControlLabel control={<Radio />}
                                            value={HolidayExtent.Full}
                                            label={t("holidayExtent.full")} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Stack>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editPausesAreWorkingTime}
                        onChange={(event) =>
                          setEditPausesAreWorkingTime(event.target.checked)
                        }
                      />
                    }
                    label={t("employeesPage.workTimeQuota.pausesAreWorkingTime")}
                  />

                  <GeneralButton
                    onClick={handleUpdate}
                    loading={isUpdating}
                    disabled={!canEdit || isUpdating}
                  >
                    {t("employeesPage.workTimeQuota.updateButton")}
                  </GeneralButton>
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <GeneralButton appVariant="secondary" onClick={onClose}>
          {t("buttons.cancel")}
        </GeneralButton>
        <GeneralButton
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!canEdit || isSubmitting}
        >
          {t("buttons.save")}
        </GeneralButton>
      </DialogActions>
    </Dialog>
  );
};

export default WorkTimeQuotaDialog;
